<script setup>
import { computed } from 'vue';
import Auth from 'dashboard/api/auth';
import { useMapGetter } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import Avatar from 'next/avatar/Avatar.vue';
import SidebarProfileMenuStatus from './SidebarProfileMenuStatus.vue';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import Icon from 'next/icon/Icon.vue';
import { LocalStorage } from 'shared/helpers/localStorage';
import { LOCAL_STORAGE_KEYS } from 'dashboard/constants/localStorage';
import { setColorTheme } from 'dashboard/helper/themeHelper.js';

import {
  DropdownContainer,
  DropdownBody,
  DropdownSeparator,
  DropdownItem,
} from 'next/dropdown-menu/base';
import CustomBrandPolicyWrapper from '../../components/CustomBrandPolicyWrapper.vue';

defineProps({
  isExpandedSidebar: { type: Boolean, default: true },
});

const emit = defineEmits(['close', 'openKeyShortcutModal']);

defineOptions({
  inheritAttrs: false,
});

const { t } = useI18n();

const currentUser = useMapGetter('getCurrentUser');
const currentUserAvailability = useMapGetter('getCurrentUserAvailability');
const accountId = useMapGetter('getCurrentAccountId');
const globalConfig = useMapGetter('globalConfig/get');
const isFeatureEnabledonAccount = useMapGetter(
  'accounts/isFeatureEnabledonAccount'
);

const showChatSupport = computed(() => {
  return (
    isFeatureEnabledonAccount.value(
      accountId.value,
      FEATURE_FLAGS.CONTACT_CHATWOOT_SUPPORT_TEAM
    ) && globalConfig.value.chatwootInboxToken
  );
});

const menuItems = computed(() => {
  return [
    {
      show: showChatSupport.value,
      showOnCustomBrandedInstance: false,
      label: t('SIDEBAR_ITEMS.CONTACT_SUPPORT'),
      icon: 'i-lucide-life-buoy',
      click: () => {
        window.$chatwoot.toggle();
      },
    },
    {
      show: true,
      showOnCustomBrandedInstance: true,
      label: t('SIDEBAR_ITEMS.KEYBOARD_SHORTCUTS'),
      icon: 'i-lucide-keyboard',
      click: () => {
        emit('openKeyShortcutModal');
      },
    },
    {
      show: true,
      showOnCustomBrandedInstance: true,
      label: t('SIDEBAR_ITEMS.PROFILE_SETTINGS'),
      icon: 'i-lucide-user-pen',
      link: { name: 'profile_settings_index' },
    },
    {
      show: true,
      showOnCustomBrandedInstance: true,
      label: t('SIDEBAR_ITEMS.APPEARANCE'),
      icon: 'i-lucide-palette',
      click: () => {
        const ninja = document.querySelector('ninja-keys');
        ninja.open({ parent: 'appearance_settings' });
      },
    },
    {
      show: true,
      showOnCustomBrandedInstance: false,
      label: t('SIDEBAR_ITEMS.DOCS'),
      icon: 'i-lucide-book',
      link: 'https://www.chatwoot.com/hc/user-guide/en',
      nativeLink: true,
      target: '_blank',
    },
    {
      show: currentUser.value.type === 'SuperAdmin',
      showOnCustomBrandedInstance: true,
      label: t('SIDEBAR_ITEMS.SUPER_ADMIN_CONSOLE'),
      icon: 'i-lucide-castle',
      link: '/super_admin',
      nativeLink: true,
      target: '_blank',
    },
    {
      show: true,
      showOnCustomBrandedInstance: true,
      label: t('SIDEBAR_ITEMS.LOGOUT'),
      icon: 'i-lucide-power',
      click: Auth.logout,
    },
  ];
});

const allowedMenuItems = computed(() => {
  return menuItems.value.filter(item => item.show);
});

function setAppearance(theme) {
  LocalStorage.set(LOCAL_STORAGE_KEYS.COLOR_SCHEME, theme);
  setColorTheme(theme === 'dark');
}

function logOut() {
  Auth.logout();
}
</script>

<template>
  <DropdownContainer
    class="relative w-full min-w-0"
    :class="{ 'z-20': isOpen }"
    @close="emit('close')"
  >
    <template #trigger="{ toggle, isOpen }">
      <button
        class="flex gap-2 items-center rounded-lg cursor-pointer text-left w-full p-1"
        :class="{
          'bg-n-brand-1': isOpen,
          'hover:bg-n-alpha-2': !isOpen,
          'align-center justify-center': !isExpandedSidebar,
        }"
        @click="toggle"
      >
        <Avatar
          :size="32"
          :name="currentUser.available_name"
          :src="currentUser.avatar_url"
          :status="currentUserAvailability"
          class="flex-shrink-0"
          rounded-full
        />
        <div v-if="isExpandedSidebar" class="min-w-0">
          <div class="text-n-brand text-sm leading-4 font-medium truncate">
            {{ currentUser.available_name }}
          </div>
          <div class="text-n-slate-5 dark:text-n-slate-10 text-xs truncate">
            {{ currentUser.email }}
          </div>
        </div>
      </button>
      <div
        class="py-2 gap-8"
        :class="
          isExpandedSidebar
            ? 'grid grid-cols-3'
            : 'flex justify-center align-center items-center'
        "
      >
        <div
          :class="isExpandedSidebar ? 'col-span-2' : 'col-span-1 max-w-[30px]'"
        >
          <div
            class="grid"
            :class="isExpandedSidebar ? 'grid-cols-2' : 'grid-cols-1'"
          >
            <div
              class="flex col-span-1 w-full dark:bg-n-brand bg-n-background-component-contrast hover:cursor-pointer"
              :class="
                isExpandedSidebar
                  ? 'rounded-l-xl justify-around'
                  : 'rounded-t-xl justify-center'
              "
            >
              <button type="button" @click="() => setAppearance('light')">
                <Icon
                  icon="i-lucide-sun"
                  class="size-4 text-n-slate-5 dark:text-n-slate-12 h-full self-center"
                />
              </button>
            </div>
            <div
              class="flex col-span-1 w-full bg-n-brand dark:bg-n-slate-10 hover:cursor-pointer"
              :class="
                isExpandedSidebar
                  ? 'rounded-r-xl justify-around'
                  : 'rounded-b-xl justify-center'
              "
            >
              <button type="button" @click="() => setAppearance('dark')">
                <Icon
                  icon="i-lucide-moon"
                  class="size-4 text-n-slate-5 dark:text-n-slate-12 h-full self-center"
                />
              </button>
            </div>
          </div>
        </div>
        <div
          v-if="isExpandedSidebar"
          class="flex justify-center col-span-1 w-full rounded-xl bg-n-brand hover:cursor-pointer"
        >
          <button type="button" @click="() => logOut()">
            <Icon
              icon="i-lucide-log-out"
              class="size-4 text-n-slate-5 dark:text-n-slate-12 h-full self-center"
            />
          </button>
        </div>
      </div>
    </template>
    <DropdownBody class="ltr:left-0 rtl:right-0 bottom-12 z-50 w-80 mb-2">
      <SidebarProfileMenuStatus />
      <DropdownSeparator />
      <template v-for="item in allowedMenuItems" :key="item.label">
        <CustomBrandPolicyWrapper
          :show-on-custom-branded-instance="item.showOnCustomBrandedInstance"
        >
          <DropdownItem v-if="item.show" v-bind="item" />
        </CustomBrandPolicyWrapper>
      </template>
    </DropdownBody>
  </DropdownContainer>
</template>
